import React, { useEffect } from 'react';
import { GridCol, Select, SelectOption } from '@flixbus/honeycomb-react';

import { Asterisk } from '../Asterisk';
import { VoucherFormSection } from '../Section';
import { VoucherFormPercentageMarketing } from './PercentageMarketing';
import { useVoucherFormContext } from '../Context';

import { Credit } from './Credit';
import { QuantityDiscount } from './QuantityDiscount';
import { RideHybrid } from './RideHybrid';
import { ValueHybrid } from './ValueHybrid';
import { Contract } from './Contract';

import { FreeAncillaries } from '../Rules/FreeAncillaries';
import { DiscountTypeMap } from '../../../types/Voucher';

import useOnMount from '../../../hooks/useInit';

import styles from '../voucher-form.module.scss';

interface VoucherFormDiscountProps {
  template?: {
    disabled: boolean;
    discountType: string;
    showFreeAncillaries: boolean;
    excludeTypes?: string[];
  };
}

const VoucherFormDiscount: React.FC<VoucherFormDiscountProps> = ({ template }) => {
  const {
    formState: {
      activeDiscount,
      payload: { discountType },
    },
    setFormState,
    handlePayloadChange,
    handleActiveDiscountChange,
  } = useVoucherFormContext();

  useOnMount(() =>
    handlePayloadChange({
      value: {
        type: template?.discountType || 'percentage_marketing',
      },
      prop: 'discountType',
    }),
  );

  useEffect(() => {
    if (template) {
      setFormState(prev => ({
        ...prev,
        activeDiscount: template?.discountType,
      }));
    }
  }, [setFormState, template]);

  const renderDiscountComponent = () => {
    switch (activeDiscount) {
      case 'percentage_marketing':
        return <VoucherFormPercentageMarketing />;
      case 'credit_amount':
      case 'value_marketing':
        return <Credit />;
      case 'free_ride_hybrid':
        return <RideHybrid />;
      case 'free_ride_marketing':
      case 'free_bike':
      case 'free_luggage':
      case 'free_seat':
        return <QuantityDiscount />;
      case 'value_hybrid':
        return <ValueHybrid />;
      default:
        return <div>Please select a valid discount type.</div>;
    }
  };

  const filteredDiscountTypes = Object.entries(DiscountTypeMap).filter(
    ([key]) => !template?.excludeTypes?.includes(key),
  );

  return (
    <VoucherFormSection
      title='Discount'
      subtitle='What the voucher offers'
      inputs={
        <>
          <GridCol className={styles.discountTypeField}>
            <Select
              id='discount-type'
              label={<>Discount type {!template?.discountType && <Asterisk />}</>}
              disabled={template?.disabled ?? false}
              defaultValue={template?.discountType || 'percentage_marketing'}
              onChange={e => handleActiveDiscountChange(e.target.value)}
              extraClasses={styles.inputLarge}
            >
              {filteredDiscountTypes.map(([key, label]) => (
                <SelectOption key={key} value={key}>
                  {label}
                </SelectOption>
              ))}
            </Select>
            {renderDiscountComponent()}
          </GridCol>
          {template?.showFreeAncillaries &&
            ['free_ride_marketing', 'free_ride_hybrid'].includes(activeDiscount) && (
              <FreeAncillaries />
            )}

          {['percentage_marketing'].includes(activeDiscount) && <FreeAncillaries />}

          {['credit_amount', 'value_marketing', 'value_hybrid'].includes(activeDiscount) &&
            discountType.currency &&
            discountType.currency === 'MXN' && <Contract />}
        </>
      }
    />
  );
};

export default VoucherFormDiscount;
